import CustomBtn from "components/fragments/CustomBtn";
import CustomInput from "components/fragments/CustomInput";
import CustomInputPassword from "components/fragments/CustomInputPassword";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { authHandler } from "../redux/handlers";
import LoginConfirmModal from "../components/modals/LoginConfirmModal";
import socketService from "../services/socketService";
import { setBranch } from "../redux/slices/pos.slice";

const validationSchema = Yup.object().shape({
  emailOrPhone: Yup.string().required("อีเมลหรือเบอร์โทรศัพท์ is required"),
  password: Yup.string().required("รหัสผ่าน is required"),
});

export default function ElectronLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState(null);

  const formik = useFormik({
    initialValues: {
      emailOrPhone: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setError(null);

        const loginResponse = await dispatch(
          authHandler.loginSlugless({
            email: values.emailOrPhone,
            password: values.password,
          })
        );

        if (loginResponse?.message === "USER_IN_IMPORTANT_TASK") {
          setPendingCredentials(values);
          setShowConfirmModal(true);
          return;
        }

        await handleLoginSuccess(loginResponse);
      } catch (err) {
        console.error("Login error:", err);
        if (err?.message === "USER_IN_IMPORTANT_TASK") {
          setPendingCredentials(values);
          setShowConfirmModal(true);
        } else {
          setError("เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง");
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleLoginSuccess = async (loginResponse) => {
    if (!loginResponse) return;

    const businessSlug = loginResponse.businessSlug;
    const osIsActive = loginResponse?.user?.branchPosSelling?.[0] != null;
    const osKitchenIsActive = loginResponse?.user?.branchKitchenSession?.[0] != null;

    if (osIsActive) {
      socketService.connect();
      const branchPosSelling = loginResponse?.user?.branchPosSelling?.[0];
      localStorage.setItem("email", loginResponse.user.email);
      localStorage.setItem("businessSlug", businessSlug);
      localStorage.setItem("branchId", branchPosSelling.branchPos.branch_id);
      localStorage.setItem("branchPOSId", branchPosSelling.branchPos.id);
      navigate(`/${businessSlug}/`);
      return;
    }

    if (osKitchenIsActive) {
      socketService.connect();
      const branchKitchenSession = loginResponse?.user?.branchKitchenSession?.[0];
      localStorage.setItem("branchKitchenId", branchKitchenSession?.branch_kitchen_id);
      localStorage.setItem("email", loginResponse.user.email);
      localStorage.setItem("businessSlug", businessSlug);
      localStorage.setItem("branchId", branchKitchenSession.branchKitchen.branch_id);
      navigate(`/${businessSlug}/kitchen`);
      return;
    }

    const branchResponse = await dispatch(authHandler.getUserBranch(businessSlug));

    if (!branchResponse || branchResponse.length === 0) {
      setError("ไม่พบข้อมูลสาขาสำหรับบัญชีนี้ กรุณาติดต่อผู้ดูแลระบบ");
      await dispatch(authHandler.logout(businessSlug));
      return;
    }

    socketService.connect();
    localStorage.setItem("email", loginResponse.user.email);
    localStorage.setItem("businessSlug", businessSlug);
    localStorage.removeItem("branchId");
    localStorage.removeItem("branchPOSId");

    if (branchResponse.length === 1) {
      dispatch(setBranch(branchResponse[0]));
      navigate(`/${businessSlug}/verify-pin`);
    } else {
      navigate(`/${businessSlug}/branch-selection`);
    }
  };

  const handleForceLogin = async () => {
    if (!pendingCredentials) return;
    try {
      setIsLoading(true);
      setError(null);

      const loginResponse = await dispatch(
        authHandler.loginSlugless({
          email: pendingCredentials.emailOrPhone,
          password: pendingCredentials.password,
        })
      );

      await handleLoginSuccess(loginResponse);
    } catch (err) {
      setError("เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง");
      console.error("Force login error:", err);
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
      setPendingCredentials(null);
    }
  };

  return (
    <div className="login-bg w-full min-h-screen flex justify-center items-center sarabun text-[var(--color-black)] p-[1vw]">
      <div className="bg-white p-[min(2.92vw,30px)] flex flex-col items-center rounded-[min(2.44vw,25px)] w-[min(39.16vw,401px)]">
        <div className="text-24 font-[700] flex flex-col items-center">
          <div className="w-[min(4.83vw,49.5px)] aspect-square">
            <img
              className="w-full h-full object-contain"
              src="/img/logo-3.png"
              alt=""
            />
          </div>
          <div className="comfortaa">
            <span className="text-[--color-grey]">Allder</span>{" "}
            <span className="text-[--color-green]">Cafe'</span>
          </div>
        </div>

        <div className="mt-[min(1.95vw,20px)] font-[700] text-20 text-[--color-blue] uppercase">
          POS
        </div>

        {error && (
          <div className="mt-[min(1.95vw,20px)] text-red-500 text-center">
            {error}
          </div>
        )}

        <form className="w-full" onSubmit={formik.handleSubmit}>
          <CustomInput
            className="mt-[min(1.95vw,20px)] w-full"
            inputprops={{
              className:
                "rounded-[min(0.78vw,8px)] border-[min(0.09vw,8px)] border-[#b6bbc6] py-[min(1.66vw,17px)] px-[min(1.95vw,20px)] pr-[min(4.2vw,42px)]",
              placeholder: "อีเมลหรือเบอร์โทรศัพท์",
              name: "emailOrPhone",
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              value: formik.values.emailOrPhone,
              error:
                formik.touched.emailOrPhone && formik.errors.emailOrPhone
                  ? "error"
                  : undefined,
              disabled: isLoading,
            }}
          />
          <CustomInputPassword
            className="mt-[min(1.95vw,20px)] w-full"
            inputprops={{
              className:
                "rounded-[min(0.78vw,8px)] border-[min(0.09vw,8px)] border-[#b6bbc6] py-[min(1.66vw,17px)] px-[min(1.95vw,20px)] pr-[min(4.2vw,42px)]",
              placeholder: "รหัสผ่าน",
              name: "password",
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              value: formik.values.password,
              error:
                formik.touched.password && formik.errors.password
                  ? "error"
                  : undefined,
              disabled: isLoading,
            }}
          />
          <CustomBtn
            type="submit"
            className="btn-main-active text-18 mt-[min(1.95vw,20px)] w-full rounded-[min(0.78vw,8px)] py-[min(1.66vw,17px)]"
            btntxt={isLoading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
            disabled={isLoading}
          />
        </form>

        <div className="mt-[min(3.22vw,30px)] text-16 flex gap-[min(0.87vw,9px)]">
          <div>Power By</div>
          <div className="aspect-square w-[min(2.05vw,21px)]">
            <img
              className="w-full h-full object-contain"
              src="/img/logo_allder_2-01.png"
              alt=""
            />
          </div>
          <div>Allder</div>
        </div>
      </div>

      <LoginConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleForceLogin}
      />
    </div>
  );
}
